import { FC, useState } from "react";
import { Button, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../../../common/routesUtils";
import { PageType } from "../../../../config/page.types";
import { useBreadcrumbs } from "../../../../context/BreadcrumbsProvider";
import UserWorkspaceSelector from "../../../../features/auth/components/UserWorkspaceSelector";
import { useAuthentication } from "../../../../hooks/useAuthentication";
import FullScreenDialog from "../../../common/FullScreenDialog";
import CompanyFiscalSelector from "./CompanyFiscalSelector";

/**
 * UserCompany renders the element that indicates the company of the current user.
 * This component should be used inside the application top bar.
 *
 * @returns the application company selector
 */
const UserCompany: FC = () => {
	const { t } = useTranslation("authentication");
	const navigateTo = useNavigate();

	const { company, availableWorkspacesCount } = useAuthentication();

	const [showSelectWorkspace, setShowSelectWorkspace] = useState<boolean>(false);
	const showWorkspaceSelector = () => setShowSelectWorkspace(true);
	const hideWorkspaceSelector = () => setShowSelectWorkspace(false);

	const { breadcrumbs } = useBreadcrumbs();
	const pagesWithoutFiscalSelector = [PageType.DataInsightsRootCause];
	const isFiscalSelectorHidden = breadcrumbs.some((breadcrumb) =>
		pagesWithoutFiscalSelector.includes(breadcrumb.pageType)
	);

	return (
		<>
			<CompanyFiscalSelector hidden={isFiscalSelectorHidden} />
			{availableWorkspacesCount <= 1 ? (
				<Tooltip title={t("you have only one workspace")} arrow>
					<span>
						<Button className="company-selector" disableRipple sx={{ pointerEvents: "none" }}>
							{company?.name}
						</Button>
					</span>
				</Tooltip>
			) : (
				<Button className="company-selector" onClick={showWorkspaceSelector}>
					{company?.name}
				</Button>
			)}

			<FullScreenDialog
				isOpen={showSelectWorkspace}
				onClose={hideWorkspaceSelector}
				isLoading={false}
				title={t("select your workspace")}
			>
				<UserWorkspaceSelector
					onCancel={hideWorkspaceSelector}
					onWorkspaceSelected={() => {
						hideWorkspaceSelector();
						navigateTo(AppRoutes.ResultsOverview);
					}}
				/>
			</FullScreenDialog>
		</>
	);
};

export default UserCompany;
